import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';
import ApprovalDataTable from '@shared/components/organisms/ApprovalDataTable/ApprovalDataTable';
import SearchFilter from '@shared/components/organisms/SearchFilter/SearchFilter';
import Pagination from '@shared/components/organisms/Pagination/Pagination';

import styles from './ContentSectionLayout.module.scss';

/**
 * ContentSectionLayout 컴포넌트
 * - 검색/필터 영역 + 결과 테이블 + 페이지네이션을 제공하는 범용 레이아웃 컴포넌트
 * - 여러 페이지에서 공통으로 사용되는 레이아웃 패턴을 통합
 *
 * @param {Array} filterFields - 검색 필터 필드 배열
 * @param {Array} tableColumns - 테이블 컬럼 정의
 * @param {Array} paginatedData - 페이지네이션된 데이터
 * @param {boolean} isEmpty - 데이터가 비어있는지 여부
 * @param {number} currentPage - 현재 페이지 번호
 * @param {number} totalPages - 전체 페이지 수
 * @param {Function} onFilterChange - 필터 값 변경 핸들러
 * @param {Function} onSearch - 검색/조회 버튼 클릭 핸들러
 * @param {Function} onReset - 필터 초기화 핸들러
 * @param {Function} onPageChange - 페이지 변경 핸들러
 * @param {Object} classes - 스타일 클래스 객체 (contentCardMarginFix, filterWrapper 등)
 * @param {ReactNode} extraContent - 검색 필터 아래에 표시할 추가 컨텐츠 (작성하기 버튼 등)
 * @param {boolean} showExcelButton - Excel 버튼 표시 여부 (기본값: false)
 * @param {string} searchButtonText - 검색 버튼 텍스트 (기본값: "검색")
 * @param {Function} onExcel - Excel 버튼 클릭 핸들러 (선택적)
 * @param {Function} onRowClick - 테이블 행 클릭 핸들러 (선택적)
 * @param {string} emptyMessage - 데이터가 없을 때 표시할 문구 (기본값: "현재 신청 내역이 없습니다.")
 */
export default function ContentSectionLayout({
  isLoading,
  isFetching,
  tableData,

  filterFields,
  tableColumns,
  paginatedData,
  isEmpty,
  currentPage,
  totalPages,
  onFilterChange,
  onSearch,
  onReset,
  onPageChange,
  classes,
  extraContent,
  showExcelButton = false,
  searchButtonText = '검색',
  emptyMessage = '현재 신청 내역이 없습니다.',
  onExcel,
  onRowClick,
  onSort,
  sortField,
}) {
  return (
    <ContentCard className={classes.contentCardMarginFix}>
      {/* 검색/필터 */}
      <div className={classes.filterWrapper}>
        <SearchFilter
          fields={filterFields}
          onFilterChange={onFilterChange}
          onSearch={onSearch}
          onReset={onReset}
          onExcel={onExcel}
          showExcelButton={showExcelButton}
          searchButtonText={searchButtonText}
        />
      </div>
      {/* 검색/필터 아래에 삽입되는 영역 */}
      {extraContent && <div className={classes.extraContentWrapper}>{extraContent}</div>}
      {/* 테이블 */}
      <div className={classes.tableWrapper}>
        {isLoading || (isFetching && tableData.length === 0) ? (
          <div className={styles.loadingContainer}>
            <div className={styles.circularProgress} />
            <div className={styles.loadingMessage}>문서 목록을 불러오는 중입니다...</div>
          </div>
        ) : (
          <>
            <ApprovalDataTable
              columns={tableColumns}
              data={paginatedData}
              onRowClick={onRowClick}
              onSort={onSort}
              sortField={sortField}
              emptyContent={emptyMessage}
            />
            {/* 로딩 중이 아닐 때만 isEmpty 메시지를 보여주도록 명시적 조건 추가 */}
            {/* {!isLoading && !isFetching && isEmpty && (
              <div className={classes.emptyCell}>{emptyMessage}</div>
            )} */}
          </>
        )}
      </div>

      {/* 페이지네이션 */}
      <div className={classes.paginationWrapper}>
        {paginatedData.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => onPageChange(page)}
            onFirstPage={() => onPageChange(1)}
            onPrevPage={() => onPageChange(Math.max(1, currentPage - 1))}
            onNextPage={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            onLastPage={() => onPageChange(totalPages)}
          />
        )}
      </div>
    </ContentCard>
  );
}
